import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CryptoJS from "crypto-js";
import { useStore } from "../store/useStore";

const encodeSourceObject = (source) =>
  CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(JSON.stringify(source)));

const setCookie = (name, value) => {
  const isLocal = window.location.hostname.includes("localhost");
  const domainAttr = isLocal ? "" : ";domain=.songjukti.com";
  const secureAttr = isLocal ? "" : ";secure";
  document.cookie = `${name}=${encodeURIComponent(value)};path=/${domainAttr}${secureAttr};samesite=lax`;
};

const checkUserMobileExists = async (mobile) => {
  try {
    const formData = new FormData();
    formData.append("query", mobile);
    const res = await fetch(
      "https://aps.songjukti.com/profile/login/api_booking/fetch_user_info.php",
      { method: "POST", body: formData }
    );
    const result = await res.json();
    return { exists: result?.p_phone === mobile && !!result?.posterId };
  } catch (error) {
    console.error("Mobile check failed:", error);
    return { exists: false };
  }
};

// Everything the login/register/forgot-password screens need, shared by
// both LoginSignUpForm (full page) and LoginSignUpFormMini (compact
// widget) so the two can never silently drift on validation rules or API
// calls the way they had started to.
export const useAuthForm = ({
  isLogin,
  setIsLogin,
  setLoading,
  setIsSubmitting,
  setIsLogged,
  handleSnackOpen,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const { setUser, ip } = useStore();
  const [isUserExist, setIsUserExist] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [verifiedUser, setVerifiedUser] = useState(null);
  const [instMessage, setInstMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const watchedPhone = watch("phone");
  const watchedPassword = watch("password");

  // Live "is this number already registered" check while signing up.
  useEffect(() => {
    const validatePhone = async () => {
      if (!isLogin && watchedPhone?.length === 11) {
        const res = await checkUserMobileExists(watchedPhone);
        if (res.exists) {
          setIsUserExist(true);
          setError("phone", {
            type: "manual",
            message: "This number is already registered.",
          });
        } else {
          setIsUserExist(false);
          clearErrors("phone");
        }
      }
    };
    validatePhone();
  }, [watchedPhone, isLogin]);

  // Auto-login from a cross-domain session cookie, if one exists.
  useEffect(() => {
    const cookieMatch = document.cookie.match(/songjukti_user=([^;]+)/);
    if (cookieMatch) {
      const cookieUser = JSON.parse(decodeURIComponent(cookieMatch[1]));
      localStorage.setItem("songjukti_user", JSON.stringify(cookieUser));
      localStorage.setItem("gh_sl@g", btoa(JSON.stringify(cookieUser)));
      setUser(cookieUser);
    }
  }, [setUser]);

  const handleAuth = async (data) => {
    setIsSubmitting(true);
    setLoading(true);

    try {
      if (isForgotPassword) {
        const identifier = data.identifier?.trim();

        if (forgotStep === 1) {
          handleSnackOpen("Verifying user...");
          const formData = new FormData();
          formData.append("phone", identifier);
          formData.append("notifyOn", "sms");
          formData.append("route", "forgetPassReq");

          const res = await fetch(
            "https://aps.songjukti.com/profile/login/api_logged/user_forget.php",
            { method: "POST", body: formData }
          );
          const result = await res.json();

          if (result?.errState === "success") {
            setVerifiedUser(result?.userId);
            setForgotStep(2);
            handleSnackOpen(result?.errMsg);
            setInstMessage(result?.errMsg);
          } else if ((result?.errRes === "error" || result?.errRes === "Warning") && result?.errMsg) {
            handleSnackOpen(result.errMsg);
          } else {
            handleSnackOpen("No user found with this email/number.");
          }
        } else if (forgotStep === 2) {
          const formData = new FormData();
          formData.append("phone", identifier);
          formData.append("token", data.otp);
          formData.append("userId", verifiedUser);
          formData.append("route", "forgetPass");

          const res = await fetch(
            "https://aps.songjukti.com/profile/login/api_logged/token_verify.php",
            { method: "POST", body: formData }
          );
          const result = await res.json();

          if (result?.errState === "success") {
            setForgotStep(3);
            handleSnackOpen(result?.errMsg);
            setInstMessage(result?.errMsg);
          } else if ((result?.errRes === "error" || result?.errRes === "Warning") && result?.errMsg) {
            handleSnackOpen(result.errMsg);
          } else {
            handleSnackOpen("Invalid OTP. Try again.");
          }
        } else if (forgotStep === 3) {
          const formData = new FormData();
          formData.append(
            "userInfo",
            JSON.stringify({
              token: data.otp,
              userId: verifiedUser,
              phone: data.identifier,
              ip,
              password: data.newPassword,
              cPassword: data.confirmNewPassword,
            })
          );

          const res = await fetch(
            "https://aps.songjukti.com/profile/login/api_logged/user_password_reset.php",
            { method: "POST", body: formData }
          );
          const result = await res.json();

          if (result?.errState === "success") {
            handleSnackOpen(result?.errMsg);
            setInstMessage(result?.errMsg);
            setIsForgotPassword(false);
            setForgotStep(1);
            setIsLogin(true);
            reset();
          } else if ((result?.errRes === "error" || result?.errRes === "Warning") && result?.errMsg) {
            handleSnackOpen(result.errMsg);
          } else {
            handleSnackOpen("Invalid request. Try again.");
          }
        }

        return;
      }

      if (isLogin) {
        const identifier = data.identifier.trim();
        const isPhone = /^[0-9]{11}$/.test(identifier);
        const formData = new FormData();

        if (isPhone) formData.append("MobileNumber", identifier);
        else formData.append("Email", identifier);

        formData.append("pass", data.password);
        formData.append("ip", ip || "");

        if (data.rememberMe) {
          localStorage.setItem(
            "RData",
            encodeSourceObject({ identifier, password: data.password })
          );
        } else {
          localStorage.removeItem("RData");
        }

        const res = await fetch("https://aps.songjukti.com/profile/fun_uinsert.php", {
          method: "POST",
          body: formData,
        });

        let result;
        try {
          result = await res.json();
        } catch {
          handleSnackOpen("Login failed. Please try again.");
          return;
        }

        if (result[0]?.sopnoid) {
          const userInfo = { ...result[0], origin: window.location.origin };
          setUser(userInfo);
          localStorage.setItem("songjukti_user", JSON.stringify(userInfo));
          localStorage.setItem("gh_sl@g", btoa(JSON.stringify(userInfo)));
          setCookie("songjukti_user", JSON.stringify(userInfo));
          handleSnackOpen(`Welcome back, ${userInfo.Name || identifier}`);
          setIsLogged(true);
        } else {
          handleSnackOpen(result?.error || "Invalid credentials or login failed.");
        }
      } else {
        const userInfo = {
          Name: data.name,
          name: data.name,
          Email: data.email,
          email: data.email,
          userPass: data.password,
          userPassC: data.confirmPassword,
          MobileNumber: data.phone,
          phone: data.phone,
          phoneCode: "+88",
          category: "",
          Location: data.location || "",
          Country: data.country || "Bangladesh",
          Address: data.address || "",
          terms: "1",
          requrstFor: "songjukti",
          sType: "1",
          ap: "",
        };

        const formData = new FormData();
        formData.append("userInfo", JSON.stringify(userInfo));
        formData.append("route", "userReg");
        formData.append("phoneCode", "+88");
        formData.append("sType", "1");
        formData.append("termsa", "1");

        const res = await fetch(
          "https://aps.songjukti.com/profile/login/api_logged/user_create_new.php",
          { method: "POST", body: formData }
        );

        const resultText = await res.text();
        let result;
        try {
          result = JSON.parse(resultText);
        } catch {
          result = resultText;
        }

        if (typeof result === "string" && result.includes("Success")) {
          const userPayload = {
            name: data.name,
            phone: data.phone,
            email: data.email,
            origin: window.location.origin,
          };
          setUser(userPayload);
          localStorage.setItem("user", JSON.stringify(userPayload));
          handleSnackOpen(`Welcome, ${data.name}!`);
          setInstMessage(`Welcome, ${data.name}!`);
          setIsLogin(true);
        } else if (result?.errRes === "error" && result?.errMsg) {
          handleSnackOpen(result.errMsg);
          setInstMessage(result.errMsg);
        } else {
          handleSnackOpen("Registration failed. Please try again.");
        }
      }
    } catch (err) {
      console.error("Auth error:", err);
      handleSnackOpen(err.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  return {
    register,
    errors,
    watch,
    reset,
    onSubmit: handleSubmit(handleAuth),
    isUserExist,
    isForgotPassword,
    setIsForgotPassword,
    forgotStep,
    setForgotStep,
    instMessage,
    showPassword,
    setShowPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    watchedPassword,
  };
};